import React, { useState, useRef } from "react";
import { Upload, FileSpreadsheet, Loader2, Save, X, CalendarDays, CheckCircle2, Clock, User, Inbox, MapPin, Download, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useGlobal } from "@/context/GlobalContext";
import { useAuth } from "@/context/AuthContext";
import { ExcelGrid, SheetTabs, exportSheetsToExcel } from "@/components/ExcelGrid";
import { SEMANAL } from "@/lib/resumenConfig";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const YEARS = Array.from({ length: 7 }, (_, i) => 2024 + i);

const MES_LOW = MESES.map((m) => m.toLowerCase());
const norm = (s) => (s || "").toString().trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
// Sustituye encabezados según filtros (título planta -> sucursal, periodo -> mes/año).
const substHeaders = (sheets, sucName, month, year, annual) => {
  if (!sheets?.length) return sheets;
  const period = annual ? null : `${MESES[month - 1]} ${year}`.toUpperCase();
  const suc = (sucName || "").toUpperCase();
  const clone = JSON.parse(JSON.stringify(sheets));
  const sh = clone[0];
  if (period) for (const row of sh.rows) for (const c of row.cells) {
    if (c.cov) continue;
    const v = String(c.v || "");
    if (v && /20\d{2}/.test(v) && MES_LOW.some((m) => v.toLowerCase().includes(m))) {
      c.v = period; if ("raw" in c) c.raw = period;
    }
  }
  if (suc) {
    let done = false;
    for (const row of sh.rows) {
      if (done) break;
      for (const c of row.cells) {
        if (c.cov) continue;
        const v = String(c.v || "");
        const isPeriod = /20\d{2}/.test(v) && MES_LOW.some((m) => v.toLowerCase().includes(m));
        if (v.trim() && v !== period && !(annual && isPeriod)) { c.v = suc; if ("raw" in c) c.raw = suc; done = true; break; }
      }
    }
  }
  return clone;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function ResultadosSemanales({ cfg = SEMANAL }) {
  const { currentStructure, structureKey, node } = useGlobal();
  const { user } = useAuth();
  const canImport = user?.is_admin || (user?.caps || []).includes(cfg.cap);
  const contexto = node?.path?.length ? node.path.map((p) => p.name).join(" › ") : "Toda la estructura";
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [active, setActive] = useState(0);
  const [targetSucName, setTargetSucName] = useState("");
  const [distAsk, setDistAsk] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const periodTxt = cfg.annual ? `${year}` : `${MESES[month - 1]} ${year}`;
  const periodParams = cfg.annual ? { year } : { month, year };

  const nodeId = node?.id ?? "entire";
  React.useEffect(() => { setPreview(null); setTargetSucName(""); setDistAsk(false); }, [structureKey, nodeId]);

  const { data: sucData } = useQuery({
    queryKey: ["resumen-sucursales", structureKey, nodeId],
    queryFn: async () => (await api.get("/resumen-semanal/sucursales", { params: { structure: structureKey, node_id: node?.id } })).data,
    enabled: !!structureKey,
    staleTime: 5 * 60 * 1000,
  });
  const sucOpts = sucData?.sucursales || [];
  // Única sucursal del alcance => importación directa. Varias => el usuario elige a cuál importar.
  const singleSuc = sucOpts.length === 1 ? sucOpts[0] : null;
  const hasScope = sucOpts.length > 0;
  const targetSuc = singleSuc || sucOpts.find((s) => s.name === targetSucName) || null;
  // Coincidencia de pestañas con sucursales del alcance (para distribución automática).
  const sucByNorm = React.useMemo(() => new Map(sucOpts.map((s) => [norm(s.name), s])), [sucOpts]);
  // Coincidencia: nombre exacto, o pestaña "Acumulado…" => nodo Acumulado (solo en Toda la estructura).
  const matchFor = (sheetName) => {
    const n = norm(sheetName);
    if (sucByNorm.has("acumulado") && n.startsWith("acumulad")) return sucByNorm.get("acumulado");
    return sucByNorm.get(n) || null;
  };
  const matchedSheets = preview ? (preview.sheets || []).filter((sh) => matchFor(sh.name)) : [];
  const canDistribute = !!preview && (preview.sheets || []).length > 1 && matchedSheets.length >= 1;

  const { data: ultimo, isLoading } = useQuery({
    queryKey: [`${cfg.qk}-ultimo`, cfg.annual ? 0 : month, year, structureKey, nodeId],
    queryFn: async () => (await api.get(`${cfg.base}/ultimo`, { params: { ...periodParams, structure: structureKey, node_id: node?.id } })).data,
    enabled: !!structureKey,
  });
  const last = ultimo?.item || null;

  // Si no hay importación del mes seleccionado, se muestra en blanco (listo para importar), sin traer otro mes.
  const shownDoc = preview ? null : last;
  const doExport = () => {
    if (!shownDoc) return;
    exportSheetsToExcel(shownDoc.sheets, `${shownDoc.sucursal_name || "resultado"}_${shownDoc.period_label || ""}`);
  };

  const handleFile = async (f) => {
    if (!f) return;
    if (!/\.(xlsx|xlsm)$/i.test(f.name)) { toast.error("Sube un archivo Excel (.xlsx)."); return; }
    if (!hasScope) { toast.error("No hay sucursales en la estructura seleccionada."); return; }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: res } = await api.post(`${cfg.base}/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res);
      setActive(0);
      if (res.detected_period) { if (!cfg.annual) setMonth(res.detected_period.month); setYear(res.detected_period.year); }
      const sheets = res.sheets || [];
      const matched = sheets.filter((sh) => matchFor(sh.name));
      if (sheets.length > 1 && matched.length >= 1) {
        setDistAsk(true);
      } else {
        // Una sola pestaña: autodetectar el nodo por el nombre de la pestaña.
        // Si no coincide, usar el nodo seleccionado actualmente (o "Acumulado" en Toda la estructura).
        if (sheets.length === 1 && !singleSuc) {
          const m = matchFor(sheets[0].name);
          setTargetSucName(m ? m.name : (node?.name || "Acumulado"));
        }
        toast.success("Excel leído. Revisa la vista previa y guárdala.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo leer el archivo.");
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const distribute = async () => {
    if (!preview) return;
    setDistributing(true);
    try {
      let n = 0;
      for (const sh of preview.sheets) {
        const suc = matchFor(sh.name);
        if (!suc) continue;
        await api.post(`${cfg.base}/save`, {
          filename: preview.filename,
          period_month: cfg.annual ? 0 : month,
          period_year: year,
          period_label: cfg.annual ? `Año ${year}` : periodTxt,
          sucursal_id: Number(suc.id) || 0,
          sucursal_name: suc.name,
          record_count: sh.nonempty_rows || 0,
          sheets: [sh],
        });
        n++;
      }
      toast.success(`Distribuido en ${n} sucursal(es) por pestaña.`);
      setPreview(null);
      setActive(0);
      setDistAsk(false);
      qc.invalidateQueries({ queryKey: [`${cfg.qk}-ultimo`] });
      qc.invalidateQueries({ queryKey: [`${cfg.qk}-historial`] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo distribuir.");
    } finally {
      setDistributing(false);
    }
  };

  const save = async () => {
    if (!preview) return;
    if (!targetSuc) { toast.error("Selecciona la sucursal a la que pertenece el archivo."); return; }
    setSaving(true);
    try {
      const { data: res } = await api.post(`${cfg.base}/save`, {
        filename: preview.filename,
        period_month: cfg.annual ? 0 : month,
        period_year: year,
        period_label: cfg.annual ? `Año ${year}` : periodTxt,
        sucursal_id: Number(targetSuc.id),
        sucursal_name: targetSuc.name,
        record_count: preview.record_count || 0,
        sheets: preview.sheets || [],
      });
      toast.success(`Importación guardada (${res.period_label}).`);
      setPreview(null);
      setActive(0);
      qc.invalidateQueries({ queryKey: [`${cfg.qk}-ultimo`] });
      qc.invalidateQueries({ queryKey: [`${cfg.qk}-historial`] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const shownSheets = preview ? substHeaders(preview.sheets, targetSuc?.name, month, year, cfg.annual) : shownDoc?.sheets;

  return (
    <div className="w-full space-y-6" data-testid={cfg.annual ? "resultado-anual" : "resultados-semanales"}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Resumen Semanal</p>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">{cfg.title}</h1>
          <p className="text-sm text-slate-500 mt-1">Consulta la última importación de la estructura seleccionada y el {cfg.annual ? "año" : "mes"} elegido, o importa un nuevo archivo Excel.</p>
        </div>
        <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" data-testid="resumen-file-input" onChange={(e) => handleFile(e.target.files?.[0])} />
        {canImport && (
          <div className="flex items-center gap-2">
            {!singleSuc && hasScope && (
              <Select value={targetSucName} onValueChange={setTargetSucName}>
                <SelectTrigger className="h-11 w-56 bg-white border-slate-200 text-sm" data-testid="resumen-import-sucursal">
                  <SelectValue placeholder="Sucursal a importar…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {sucOpts.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}{s.is_leaf === false && s.label ? ` · ${s.label}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <button onClick={() => fileRef.current?.click()} disabled={parsing || !hasScope} data-testid="resumen-import-btn"
              title={!hasScope ? "No hay sucursales en la estructura" : ""}
              className="h-11 px-5 rounded-xl bg-red-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-50 shadow-sm">
              {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Importar Excel
            </button>
          </div>
        )}
      </div>

      {/* Contexto de estructura */}
      <div className="flex items-center gap-2 text-sm bg-white rounded-2xl border border-slate-200/70 shadow-sm px-4 py-3" data-testid="resumen-context">
        <MapPin className="w-4 h-4 text-red-600 shrink-0" />
        <span className="text-slate-400 font-semibold uppercase text-[10px] tracking-widest">Contexto</span>
        <span className="font-semibold text-slate-800 truncate">{currentStructure?.name || "—"}</span>
        <span className="text-slate-300">·</span>
        <span className="text-slate-600 truncate">{contexto}</span>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 flex flex-wrap items-center gap-3" data-testid="resumen-filters">
        {singleSuc ? (
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-red-500" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Sucursal</span>
            <span className="text-sm font-semibold text-slate-800" data-testid="resumen-sucursal-label">{singleSuc.name}</span>
          </div>
        ) : hasScope ? (
          <div className="flex items-center gap-2 text-sm text-amber-700" data-testid="resumen-multi-scope">
            <MapPin className="w-4 h-4" />
            <span>{sucOpts.length} sucursales en el alcance{targetSuc ? ` · importando a: ${targetSuc.name}` : " · consultando la más reciente"}</span>
          </div>
        ) : null}
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-slate-400" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Periodo</span>
          {!cfg.annual && (
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-9 w-36 bg-slate-50 border-slate-200 text-sm" data-testid="resumen-month"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{MESES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-9 w-24 bg-slate-50 border-slate-200 text-sm" data-testid="resumen-year"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {preview && <span className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1 font-semibold">Vista previa · sin guardar</span>}
      </div>

      {preview && (
        <>
          <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 flex flex-wrap items-center gap-4" data-testid="resumen-preview-bar">
            <div className="flex items-center gap-2 text-slate-700">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              <span className="font-semibold text-sm truncate max-w-[240px]" data-testid="resumen-filename">{preview.filename}</span>
            </div>
            <span className="text-slate-300">·</span>
            <span className="text-sm text-slate-500" data-testid="resumen-record-count">{preview.record_count} registros</span>
            <span className="text-slate-300">·</span>
            <span className="text-sm text-slate-600 font-semibold">{singleSuc?.name}</span>
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={() => { setPreview(null); setActive(0); setDistAsk(false); }} data-testid="resumen-cancel" className="h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 flex items-center gap-1.5 hover:bg-slate-50"><X className="w-4 h-4" />Descartar</button>
              <button onClick={save} disabled={saving} data-testid="resumen-save" className="h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Guardar importación</button>
            </div>
          </div>
          {preview.detected_period && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-xl px-4 py-2.5" data-testid="resumen-detected">
              <CheckCircle2 className="w-4 h-4" />Periodo detectado: <b>{preview.detected_period.label}</b>. Ajusta el periodo arriba antes de guardar.
            </div>
          )}
        </>
      )}

      {!preview && last && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 bg-white rounded-2xl border border-slate-200/70 shadow-sm px-4 py-3" data-testid="resumen-last-info">
          <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700"><FileSpreadsheet className="w-4 h-4 text-emerald-600" />{last.filename}</span>
          <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700"><MapPin className="w-3.5 h-3.5 text-red-500" />{last.sucursal_name}</span>
          <span className="inline-flex items-center gap-1.5">{last.record_count} registros</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{fmtDate(last.imported_at)}</span>
          <span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{last.imported_by_name || last.imported_by_email || "—"}</span>
          <button onClick={doExport} data-testid="resumen-export" className="ml-auto h-8 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-emerald-700 transition-colors"><Download className="w-3.5 h-3.5" />Exportar a Excel</button>
        </div>
      )}

      {!structureKey ? (
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-12 text-center" data-testid="resumen-pick-suc">
          <div className="inline-flex p-4 rounded-2xl bg-slate-50 text-slate-400"><MapPin className="w-8 h-8" /></div>
          <p className="mt-4 text-slate-600 font-semibold">Selecciona una estructura para comenzar.</p>
          <p className="text-sm text-slate-400 mt-1">El reporte usa automáticamente la estructura elegida en el sistema.</p>
        </div>
      ) : isLoading && !preview ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 text-red-600 animate-spin" /></div>
      ) : shownSheets ? (
        <div className="space-y-3">
          <SheetTabs sheets={shownSheets} active={active} onChange={setActive} />
          <ExcelGrid sheet={shownSheets[active]} />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-12 text-center" data-testid="resumen-empty">
          <div className="inline-flex p-4 rounded-2xl bg-slate-50 text-slate-400"><Inbox className="w-8 h-8" /></div>
          <p className="mt-4 text-slate-600 font-semibold">No hay importación {singleSuc ? `de ${singleSuc.name} ` : ""}para {periodTxt}.</p>
          <p className="text-sm text-slate-400 mt-1">{canImport ? (singleSuc ? 'Usa el botón "Importar Excel" para cargar un archivo.' : 'Selecciona la sucursal y usa el botón "Importar Excel" para cargar un archivo.') : "No hay importación disponible para este periodo."}</p>
        </div>
      )}

      {distAsk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="distribute-dialog">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <h3 className="text-lg font-extrabold text-slate-900">Pestañas por sucursal detectadas</h3>
            <p className="text-sm text-slate-600 mt-2">El archivo contiene múltiples pestañas cuyos nombres coinciden con sucursales de la estructura organizacional seleccionada. ¿Desea que el sistema distribuya automáticamente la información de cada pestaña en la sucursal correspondiente?</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {matchedSheets.map((sh) => (
                <span key={sh.name} className="text-xs font-semibold px-2 py-1 rounded-lg bg-red-50 text-red-700 border border-red-200">{sh.name}</span>
              ))}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button onClick={() => setDistAsk(false)} disabled={distributing} data-testid="distribute-no" className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">No, importar todo en la sucursal seleccionada</button>
              <button onClick={distribute} disabled={distributing} data-testid="distribute-yes" className="h-10 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-red-700 disabled:opacity-50">{distributing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Sí, distribuir por sucursal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
